import React from "react";
import { Droplets, Sparkles } from "lucide-react";

// Shared brand constants
export const BRAND = {
  name: "WashPros",
  tagline: "The Carwash Concierge Company",
  slogan: "Wax And Shine Headquarters",
  website: "washpros.io",
  email: "hello@washpros.io",
  address: "6800 Bird Rd Suite 454, Miami, FL 33155",
  version: "1.0.0",
};

// Both slogans for use throughout the app — Brand Asset Lock
// (Phase 7g.8): every approved slogan must carry its trademark
// symbol. Display callsites should render these strings as-is.
export const SLOGANS = {
  primary: "THE CARWASH CONCIERGE COMPANY™",
  secondary: "WAX AND SHINE HEADQUARTERS™",
};

// Shared color scheme
export const COLORS = {
  primary: {
    cyan: "#06B6D4",
    blue: "#3B82F6",
  },
  secondary: {
    gold: "#D4AF37",
    amber: "#F59E0B",
  },
  vehicle: {
    car: "#06B6D4",     // cyan
    boat: "#3B82F6",    // blue
    rv: "#A855F7",      // purple
    fleet: "#F59E0B",   // amber
  },
  vendor: {
    mobile: "#06B6D4",       // cyan
    facility: "#8B5CF6",     // purple
  }
};

// Dynamic stats - these should be fetched from API in production
// Set to null to hide when no real data is available
export const PLATFORM_STATS = {
  customers: null,  // Will be populated from API
  vendors: null,    // Will be populated from API
  rating: "4.9",    // Keep rating as it's a target
  washes: null,     // Will be populated from API
};

// Service categories
export const SERVICE_CATEGORIES = [
  { id: "car", label: "Car", color: COLORS.vehicle.car },
  { id: "boat", label: "Boat", color: COLORS.vehicle.boat },
  { id: "rv", label: "RV", color: COLORS.vehicle.rv },
  { id: "fleet", label: "Fleet", color: COLORS.vehicle.fleet },
];

// Shared Logo Component
export const WashProsLogo = ({ size = "default", showTagline = false, showWebsite = false, showBothSlogans = false }) => {
  const sizes = {
    small: { icon: "w-8 h-8", iconInner: "w-4 h-4", text: "text-lg" },
    default: { icon: "w-10 h-10", iconInner: "w-5 h-5", text: "text-xl" },
    large: { icon: "w-12 h-12", iconInner: "w-6 h-6", text: "text-2xl" },
    xlarge: { icon: "w-16 h-16", iconInner: "w-8 h-8", text: "text-4xl" },
  };

  const s = sizes[size] || sizes.default;

  return (
    <div className="flex flex-col items-center">
      <div className="flex items-center gap-2">
        <div className={`${s.icon} rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center`}>
          <Droplets className={`${s.iconInner} text-white`} />
        </div>
        <span className={`washpros-logo ${s.text}`}>
          <span className="wash">Wash</span>
          <span className="pros">Pros</span>
        </span>
      </div>
      {showBothSlogans && (
        <div className="text-center mt-2">
          <p className="text-amber-400 text-xs font-bold tracking-wider">{SLOGANS.secondary}</p>
          <p className="text-cyan-400 text-xs font-medium tracking-wide mt-0.5">{SLOGANS.primary}</p>
        </div>
      )}
      {showTagline && !showBothSlogans && (
        <p className="text-slate-400 text-sm mt-1">{BRAND.tagline}</p>
      )}
      {showWebsite && (
        <p className="text-cyan-400 text-xs font-medium mt-1">{BRAND.website}</p>
      )}
    </div>
  );
};

// Dual Slogan Banner Component
export const DualSloganBanner = ({ variant = "default" }) => {
  const variants = {
    default: "py-3",
    compact: "py-2",
    hero: "py-6",
  };

  return (
    <div className={`text-center ${variants[variant]}`}>
      <div className="flex items-center justify-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-amber-400" />
        <p className="text-amber-400 text-sm md:text-base font-bold tracking-wider uppercase">
          {SLOGANS.secondary}
        </p>
        <Sparkles className="w-4 h-4 text-amber-400" />
      </div>
      <p className="text-cyan-400 text-xs md:text-sm font-medium tracking-wide uppercase">
        {SLOGANS.primary}
      </p>
    </div>
  );
};

// Multi-Vehicle Logo Component
export const MultiVehicleLogo = ({ size = "default" }) => {
  const sizes = {
    small: { container: "w-32 h-24", main: "w-14 h-14", side: "w-10 h-10", iconMain: "w-7 h-7", iconSide: "w-5 h-5" },
    default: { container: "w-48 h-32", main: "w-20 h-20", side: "w-16 h-16", iconMain: "w-10 h-10", iconSide: "w-8 h-8" },
    large: { container: "w-64 h-44", main: "w-28 h-28", side: "w-20 h-20", iconMain: "w-14 h-14", iconSide: "w-10 h-10" },
  };

  const s = sizes[size] || sizes.default;

  return (
    <div className={`relative ${s.container}`}>
      {/* Car - Center Front */}
      <div className={`absolute left-1/2 bottom-0 -translate-x-1/2 ${s.main} rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 z-30`}>
        <svg className={s.iconMain} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" fill="white"/>
        </svg>
      </div>
      
      {/* Boat - Left Back */}
      <div className={`absolute left-2 top-2 ${s.side} rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30 z-20 transform -rotate-6`}>
        <svg className={s.iconSide} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l1.89-6.68c.08-.26.06-.54-.06-.78s-.34-.42-.6-.5L20 10.62V6c0-1.1-.9-2-2-2h-3V1H9v3H6c-1.1 0-2 .9-2 2v4.62l-1.29.42c-.26.08-.48.26-.6.5s-.15.52-.06.78L3.95 19z" fill="white"/>
        </svg>
      </div>
      
      {/* RV - Right Back */}
      <div className={`absolute right-2 top-2 ${s.side} rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/30 z-20 transform rotate-6`}>
        <svg className={s.iconSide} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 4h-4V3c0-.55-.45-1-1-1h-2c-.55 0-1 .45-1 1v1H6c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2V8l-4-4zm-9 14.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm10 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1-4.5h-6V8h4l2 2v4z" fill="white"/>
        </svg>
      </div>

      {/* Water droplet */}
      <div className="absolute -top-2 left-1/2 -translate-x-1/2">
        <Droplets className="w-6 h-6 text-cyan-400 animate-bounce" />
      </div>
    </div>
  );
};

// Beta Banner Component - REMOVED FOR PRODUCTION
// This component is intentionally empty to remove prototype signals
export const BetaBanner = ({ onClose }) => null;

// Service Type Pills Component
export const ServiceTypePills = ({ selected, onSelect, showAll = true }) => {
  const types = showAll 
    ? [{ id: "all", label: "All", color: "#94A3B8" }, ...SERVICE_CATEGORIES]
    : SERVICE_CATEGORIES;

  return (
    <div className="flex gap-2 overflow-x-auto pb-2">
      {types.map((type) => (
        <button
          key={type.id}
          onClick={() => onSelect?.(type.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
            selected === type.id
              ? "bg-cyan-500 text-white"
              : "bg-slate-800 text-slate-400 hover:bg-slate-700"
          }`}
          style={selected === type.id ? {} : { borderColor: type.color }}
        >
          {type.label}
        </button>
      ))}
    </div>
  );
};

export default {
  BRAND,
  SLOGANS,
  COLORS,
  PLATFORM_STATS,
  SERVICE_CATEGORIES,
  WashProsLogo,
  MultiVehicleLogo,
  BetaBanner,
  DualSloganBanner,
  ServiceTypePills,
};
